import React, { ChangeEvent, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  MdmDeviceDto,
  MdmObject,
  useDeleteDeviceMutation,
  useGetObjectsByProjectQuery,
  useGetProjectQuery,
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { getFormErrorMessage } from "../utils/utils";
import AdditionalInfo from "./AdditionalInfo";
import DatasetsTab from "./DatasetsTab";
import DeviceStatus from "./DeviceStatus";

type DeviceFormProps = {
  device: Partial<MdmDeviceDto> | null;
  onSubmit: (device: Partial<MdmDeviceDto>) => void;
  newForm?: boolean;
  isDialog?: boolean;
  closeDialog?: () => void;
};

type DeviceFormType = { identifier: string; mdmObject: Partial<MdmObject> };

const defaultDevice: Partial<MdmDeviceDto> = {
  tags: [],
};

const schema = z.object({
  identifier: z.string().min(1, "Required"),
  mdmObject: z.object({
    title: z.string(),
    id: z.string(),
  }),
});

const DeviceForm: React.FC<DeviceFormProps> = ({
  device,
  onSubmit,
  newForm = false,
  isDialog = false,
  closeDialog,
}) => {
  const [editable, setEditable] = useState(newForm);
  const [deviceState, setDeviceState] = useState<Partial<MdmDeviceDto>>(device ?? defaultDevice);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DeviceFormType>({
    resolver: zodResolver(schema),
  });

  const { projectId } = useParams();

  const { data: projectData } = useGetProjectQuery({ variables: { id: projectId! }, skip: !projectId });
  const deviceAttributes = projectData?.mdmProjectById?.configuration?.deviceConfiguration.customFields ?? [];
  const deviceTypes = projectData?.mdmProjectById?.configuration?.deviceConfiguration.measurementTypes ?? [];

  const { data: objectData } = useGetObjectsByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });
  const [deleteDeviceMutation] = useDeleteDeviceMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device deleted successfully",
        life: 3000,
      });
      navigate(-1);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetObject"],
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement> | ChipsChangeParams) => {
    const { name, value } = e.target;
    setDeviceState(prev => ({ ...prev, [name]: value }));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this device?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteDeviceMutation({
          variables: {
            id: deviceState.id!,
          },
          update(cache) {
            const normalizedId = cache.identify({ id: deviceState.id, __typename: "MdmDeviceDTO" });
            cache.evict({ id: normalizedId });
            cache.gc();
          },
        });
      },
    });
  };

  const handleDeviceTypeChange = (e: DropdownChangeParams) => {
    setDeviceState(prev => ({ ...prev, objectRelation: { ...prev.objectRelation, roleInObject: e.value} }));
  }

  const handleDeviceConfigChange = (e: DropdownChangeParams) => {
    const config = projectData?.mdmProjectById?.deviceConfigs?.find(
      cfg => cfg?.id === e.value
    );
    setDeviceState(prev => ({ ...prev, config }));
  }

  const renderIdentifierControl = (field: ControllerRenderProps<DeviceFormType, any>) => (
    <InputLabelWrapper labelName="Identifier" col={2} >
      <InputText
        value={field.value}
        onChange={e => {
          field.onChange(e.target.value);
          handleChange(e);
        }}
        className="p-inputtext-sm"
        name="identifier"
        placeholder="Identifier"
        disabled={!editable}
      />
      {getFormErrorMessage("identifier", errors)}
    </InputLabelWrapper>
  );

  const renderMdmObjectControl = (field: ControllerRenderProps<DeviceFormType, any>) => (
    <InputLabelWrapper labelName="Object" col={3} >
      <Dropdown
        value={field.value}
        options={objectData?.mdmObjectsByProjectId.map(object => ({
          title: object.title,
          id: object.id,
        }))}
        onChange={e => {
          field.onChange(e.value);
          setDeviceState(prev => ({ ...prev, objectRelation: { ...prev.objectRelation, mdmObject: e.value } }));
        }}
        optionLabel="title"
        name="mdmObject"
        placeholder="Object"
        className="p-inputtext-sm"
        disabled={!editable}
        filter
      />
      {errors.mdmObject?.title && (
        <small className="p-error">{errors.mdmObject.title.message}</small>
      )}
    </InputLabelWrapper>
  );

  return (
    <div id="device-form" className="mx-auto">
      <ConfirmPopup />
      <form className="flex justify-content-between flex-column gap-5">
        <div className="grid p-fluid">
          <Controller
            defaultValue={deviceState.identifier ?? ""}
            control={control}
            name="identifier"
            render={({ field }) => renderIdentifierControl(field)}
          />
          <Controller
            defaultValue={{
              title: deviceState.objectRelation?.mdmObject?.title!,
              id: deviceState.objectRelation?.mdmObject?.id,
            }}
            control={control}
            name="mdmObject"
            render={({ field }) => renderMdmObjectControl(field)}
          />
          <div className="text-left col-1 mt-5">
            {deviceState.objectRelation?.mdmObject?.id && (
              <Link
                to={`/${projectId}/objects/${deviceState.objectRelation?.mdmObject?.id}`}
                title="Object Info"
              >
                <i className="pi pi-external-link mt-4" />
              </Link>
            )}
          </div>
          <InputLabelWrapper labelName="Type" col={2}>
            <Dropdown
              value={deviceState.objectRelation?.roleInObject}
              options={deviceTypes ?? []}
              optionLabel="title"
              optionValue="id"
              className="p-inputtext-sm"
              onChange={handleDeviceTypeChange}
              showClear
              disabled={!editable}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Configuration" col={2}>
            <Dropdown
              value={deviceState.config?.id}
              options={projectData?.mdmProjectById?.deviceConfigs ?? []}
              optionLabel="title"
              optionValue="id"
              className="p-inputtext-sm"
              onChange={handleDeviceConfigChange}
              disabled={!editable}
            />
          </InputLabelWrapper>
          <div className="col-2 mt-2 flex justify-content-end align-items-start gap-2">
            {!editable && (
              <Button
                type="button"
                icon="pi pi-pencil"
                title="Edit"
                className="p-button-rounded p-button-text"
                onClick={() => setEditable(true)}
              />
            )}
            {editable && !newForm && (
              <Button
                type="button"
                icon="pi pi-trash"
                title="Delete"
                className="p-button-rounded p-button-text p-button-danger"
                onClick={handleDelete}
              />
            )}
            {device && <DeviceStatus device={device as MdmDeviceDto} />}
          </div>
          <InputLabelWrapper labelName="Tags" col={12}>
            <Chips
              value={deviceState.tags ?? []}
              onChange={handleChange}
              className="p-inputtext-sm"
              name="tags"
              placeholder="Tags.."
              disabled={!editable}
            />
          </InputLabelWrapper>
          {!newForm && (
            <>
              <InputLabelWrapper labelName="Created at" col={4}>
                <InputText
                  value={new Date(deviceState.createdAt).toLocaleString() ?? ""}
                  className="p-inputtext-sm"
                  name="createdAt"
                  disabled
                />
              </InputLabelWrapper>
              <InputLabelWrapper labelName="Updated at" col={4}>
                <InputText
                  value={new Date(deviceState.modifiedAt).toLocaleString() ?? ""}
                  className="p-inputtext-sm"
                  name="modifiedAt"
                  disabled
                />
              </InputLabelWrapper>
            </>
          )}
          {deviceAttributes.length > 0 && (
            <AdditionalInfo
              attributes={deviceAttributes}
              objectState={deviceState}
              setObjectState={setDeviceState}
              editable={editable}
            />
          )}
        </div>
      </form>
      {device && !newForm && (
        <TabView>
          <TabPanel header="Datasets">
            <DatasetsTab datasets={device.boundDatasets?.all} />
          </TabPanel>
        </TabView>
      )}
      <div className="flex w-full justify-content-end gap-3">
        {isDialog && !editable && (
          <Button
            type="button"
            label="Close"
            onClick={closeDialog}
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
        {editable && (
          <Button
            type="submit"
            onClick={handleSubmit(() => onSubmit(deviceState))}
            label={newForm ? "Create" : "Save"}
            className={`p-button-rounded p-button-${newForm ? "success" : "primary"} w-10rem`}
          />
        )}
        {device && !newForm && editable && (
          <Button
            type="button"
            onClick={() => {
              setEditable(false);
              setDeviceState(device ?? {});
            }}
            label="Cancel"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
        {((newForm && !device) || !editable) && !isDialog && (
          <Button
            type="button"
            onClick={() => navigate(-1)}
            label="Back"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
      </div>
    </div>
  );
};

export default DeviceForm;
